import { useEffect, useRef, useState } from 'react';
import { ghs } from '../lib/money.js';
import { Button } from '../components/ui.jsx';
import { printElement, RECEIPT_PAGE } from '../lib/print.js';
import { onTill, printReceipt, printerProblem, printerQueue, queueProblem, openDrawer } from '../lib/till.js';

/**
 * 80mm layout. The cashier who rang the sale up prints on every receipt, and the staff member
 * who performed each line prints against that line wherever the business credits one. Both
 * come from snapshots, so a reprint next year still names the right people.
 *
 * The head and the foot come off the sale, so the laundry's receipt is headed EZEL LAUNDRY
 * and the spa's is headed the salon. Everything here is deliberately the same shape as the
 * ESC/POS renderer in server/src/escpos.js: the only difference between them should be how
 * the ink gets on the paper.
 */
export function Receipt({ sale, onNew, cashierName, autoPrint = true }) {
  const cashier = cashierName || sale.cashier_name || sale.sold_by_name || 'Front desk';
  // Carried on the sale by readSale, so this and the ESC/POS renderer cannot drift apart.
  const creditsStaff = sale.credits_staff !== false;
  const header = sale.receipt_header
    || ['EZEL LUXURY SALON & SPA', 'Adjacent Ezel Fashion Gh', 'Oyarifa, Accra', '+233 53 995 5161'];
  const footer = sale.receipt_footer
    || ['Thank you for visiting Ezel Place', 'ezelluxurysalonandspa.com'];
  const paper = useRef(null);
  const [status, setStatus] = useState(null);

  // The reference becomes the filename when the browser saves to PDF.
  const onPaper = () => printElement(paper.current, { title: sale.ref, page: RECEIPT_PAGE });

  const print = async () => {
    setStatus({ busy: true });
    try {
      const r = await printReceipt(sale, { fallback: onTill ? undefined : onPaper });
      setStatus({ ...r, problem: printerProblem(r) });

      // Accepted by the spooler is not the same as printed. If the queue is not moving, say
      // so here rather than letting the next sixty seven receipts pile up behind it.
      if (r.sent && onTill) {
        const q = await printerQueue().catch(() => null);
        const stuck = queueProblem(q);
        if (stuck) setStatus(s => ({ ...s, stuck, queued: q.queued }));
      }
    } catch (e) {
      // Nothing here may end without a status. A receipt screen that says nothing is a
      // cashier deciding for themselves whether the paper is coming.
      setStatus({ sent: false, error: e.message, problem: `The till could not print: ${e.message}` });
    }
  };

  // On a till the receipt prints itself. Nobody at a counter should have to press a button
  // to get the piece of paper the customer is already waiting for. In a browser it does not,
  // because a manager opening a past sale does not want a print dialog in their face.
  /*
   * The drawer, on a button.
   *
   * It used to open by itself whenever a sale took cash. That reads well and is wrong in a
   * salon: the receipt prints the moment the sale is saved, and the cashier is often not
   * standing at the drawer then. A drawer that opens with nobody in front of it is a drawer
   * standing open, which is the one thing a cash drawer must never be.
   *
   * So the person decides. Change is given when the customer is there.
   */
  const [drawer, setDrawer] = useState(null);
  const kick = async () => {
    setDrawer({ busy: true });
    const r = await openDrawer();
    setDrawer({ ...r, problem: printerProblem(r) });
  };

  const fired = useRef(false);
  useEffect(() => {
    if (!onTill || !autoPrint || fired.current) return;
    fired.current = true;
    print();
  }, [sale.id]);

  return (
    <div style={{ display: 'grid', placeItems: 'center', gap: 20, padding: 24 }}>
      <div style={{ textAlign: 'center' }} className="no-print">
        <div className="t-heading-xl" style={{ color: 'var(--color-status-success)' }}>Sale complete</div>
        <div className="money t-money-l secondary">{sale.ref}</div>
      </div>

      {/* the paper, always light: a thermal printer only puts black ink on white */}
      <div id="receipt" ref={paper} className="print-80mm" style={{
        width: 384, background: '#ffffff', color: '#020617', padding: '20px 16px',
        boxShadow: 'var(--elevation-md)', borderRadius: 4
      }}>
        <Line center bold>{header[0]}</Line>
        {header.slice(1).map((l, i) => <Line key={i} center>{l}</Line>)}
        <Rule />
        <Split left={sale.ref} right={new Date(sale.sold_at).toLocaleDateString('en-GB')} />
        <Split left={`Served by ${cashier}`}
          right={new Date(sale.sold_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} />
        <Rule />

        {sale.items.map(it => (
          <div key={it.id} style={{ marginBottom: 6 }}>
            <Line>{it.name}</Line>
            <Split left={`  ${it.qty} x ${ghs(it.unit_price, { symbol: false })}`} right={ghs(it.line_total, { symbol: false })} />
            {/* Who did the work, at full ink. A thermal head is one bit per dot: grey text
                dithers into something patchy or drops out altogether, and this is the line
                a client reads to know who to ask for next time. Left off entirely where the
                business credits nobody, so the paper receipt and this one say the same. */}
            {creditsStaff && <Line>{it.staff_name ? `  by ${it.staff_name}` : '  by the salon'}</Line>}
          </div>
        ))}

        <Rule />
        <Split left="Subtotal" right={ghs(sale.subtotal, { symbol: false })} />
        {sale.discount_total > 0 && <Split left="Discount" right={'-' + ghs(sale.discount_total, { symbol: false })} />}
        <Split left="TOTAL GHS" right={ghs(sale.total, { symbol: false })} big />
        <Rule />

        {sale.payments.map(p => (
          <Split key={p.id}
            left={p.method === 'momo' ? `MoMo ${p.network || ''} ${p.reference || ''}`.trim() : cap(p.method)}
            right={ghs(p.amount, { symbol: false })} />
        ))}
        {sale.balance > 0 && <Split left="Balance owed" right={ghs(sale.balance, { symbol: false })} />}
        {sale.tip_total > 0 && <Split left={`Tip to ${sale.tips[0]?.staff_name || 'staff'}`} right={ghs(sale.tip_total, { symbol: false })} />}

        <Rule />
        {/* Two names, every time: who performed each line, above, and who took the money,
            here. Both are snapshots on the sale, so a reprint next year names the same
            people even after they have left. */}
        <Split left="Cashier" right={cashier} />
        <div style={{ height: 10 }} />
        {footer.map((l, i) => <Line key={i} center dim>{l}</Line>)}
      </div>

      <div style={{ display: 'flex', gap: 12 }} className="no-print">
        <Button variant="secondary" onClick={print} disabled={status?.busy}>
          {status?.busy ? 'Printing...' : onTill ? 'Print again' : 'Print receipt'}
        </Button>
        {onTill && status?.problem &&
          <Button variant="secondary" onClick={onPaper}>Print on the screen printer instead</Button>}
        {onTill && (
          <Button variant="secondary" onClick={kick} disabled={drawer?.busy}>
            {drawer?.busy ? 'Opening' : 'Open the drawer'}
          </Button>
        )}
        <Button onClick={onNew}>New sale</Button>
      </div>

      {drawer?.problem && (
        <div className="t-body-s no-print" style={{ maxWidth: 480, textAlign: 'center',
             color: 'var(--color-status-danger)' }}>
          The drawer did not open. {drawer.problem}
        </div>
      )}

      <div className="t-body-s no-print" style={{ maxWidth: 480, textAlign: 'center' }}>
        {onTill && !status
          // On a till this should never be seen for more than a moment. If it stays, the
          // printing was never attempted, which is a different fault from a printer that
          // refused, and says so rather than looking like a printer that is simply slow.
          ? <span style={{ color: 'var(--color-status-warning)' }}>
              Printing has not started. The sale is saved as {sale.ref}. Press Print again.
            </span>
          : status?.problem
          // Said out loud, not swallowed. The sale is already on the books either way, and a
          // cashier who thinks a receipt printed when it did not will not go and fix it.
          ? <span style={{ color: 'var(--color-status-danger)' }}>
              {status.problem} The sale is saved as {sale.ref} and can be printed again.
              {status.error ? <><br /><span className="muted">({status.error})</span></> : null}
            </span>
          : status?.stuck
            ? <span style={{ color: 'var(--color-status-warning)' }}>
                {sale.ref} went to the printer, and the printer is not printing. {status.stuck}
              </span>
          : status?.busy
            ? <span className="muted">Printing {sale.ref}...</span>
          : status?.sent && onTill
            ? <span className="muted">
                Printed on the till{status.bytes ? `, ${status.bytes} bytes over ${status.transport} to ${status.target}` : ''}.
                {' '}Press Print again for another copy.
              </span>
            : <span className="muted">
                Prints the receipt alone on 80mm paper, not the screen around it. Saving to PDF
                names the file {sale.ref}.
              </span>}
      </div>
    </div>
  );
}

const mono = { fontFamily: 'var(--font-money)', fontSize: 13, lineHeight: '20px' };

const Line = ({ children, center, bold, dim }) => (
  <div style={{ ...mono, textAlign: center ? 'center' : 'left', fontWeight: bold ? 600 : 400, opacity: dim ? 0.6 : 1 }}>
    {children}
  </div>
);

const Split = ({ left, right, big }) => (
  <div style={{ ...mono, display: 'flex', gap: 8, fontSize: big ? 20 : 13, fontWeight: big ? 600 : 400, lineHeight: big ? '28px' : '20px' }}>
    <span style={{ flex: 1 }}>{left}</span>
    <span>{right}</span>
  </div>
);

const Rule = () => <div style={{ ...mono, opacity: 0.4, whiteSpace: 'nowrap', overflow: 'hidden' }}>{'-'.repeat(48)}</div>;

const cap = s => String(s).charAt(0).toUpperCase() + String(s).slice(1);
